import { useEffect, useState } from 'react';

interface LightboxImage {
  src: string;
  alt?: string;
}

interface Props {
  images: LightboxImage[];
  initialIndex?: number;
  onClose: () => void;
}

export function Lightbox({ images, initialIndex = 0, onClose }: Props) {
  const [idx, setIdx] = useState(initialIndex);

  const next = () => setIdx((i) => (i + 1) % images.length);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft')  prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);

  if (images.length === 0) return null;
  const img = images[idx];

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-sm grid place-items-center p-4"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Cerrar"
        className="absolute top-5 right-5 h-10 w-10 grid place-items-center text-white/70 hover:text-white text-2xl"
      >
        <i className="fas fa-times" />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Anterior"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center text-white/70 hover:text-orange text-2xl bg-black/40 border border-white/10 rounded-full"
          >
            <i className="fas fa-chevron-left" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Siguiente"
            className="absolute right-3 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center text-white/70 hover:text-orange text-2xl bg-black/40 border border-white/10 rounded-full"
          >
            <i className="fas fa-chevron-right" />
          </button>
        </>
      )}

      <img
        src={img.src}
        alt={img.alt ?? ''}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] object-contain border border-white/10 shadow-2xl"
      />

      {images.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 font-display text-white/60 tracking-wider2 text-sm">
          {idx + 1} / {images.length}
        </div>
      )}
    </div>
  );
}
